import React from "react";
import { MapPin, CheckCircle, Sparkles } from "lucide-react";
import defaultAvatar from "../../utils/profile.png";
import { formatHeight } from "../../utils/formatHeight";
import FeaturedBadge from "../plans/FeaturedBadge";

// Colores por deporte
const SPORT_COLORS = {
  Fútbol: "bg-green-100 text-green-700",
  Baloncesto: "bg-orange-100 text-orange-700",
  Tenis: "bg-yellow-100 text-yellow-700",
  Natación: "bg-cyan-100 text-cyan-700",
  Atletismo: "bg-purple-100 text-purple-700",
};

// Colores por categoría
const CATEGORY_COLORS = {
  Profesional: "bg-blue-100 text-blue-700",
  "Semi-Profesional": "bg-indigo-100 text-indigo-700",
  Amateur: "bg-gray-100 text-gray-600",
  Recreativo: "bg-slate-100 text-slate-600",
  Élite: "bg-violet-100 text-violet-700",
};

// Badge según el estado de la solicitud de contacto del scout a este atleta.
const CONTACT_STATUS_BADGE = {
  pending: { label: "Solicitud enviada", className: "bg-amber-100 text-amber-700" },
  guardian_pending: { label: "Pendiente tutor", className: "bg-purple-100 text-purple-700" },
  approved: { label: "Conectado", className: "bg-green-100 text-green-700" },
  rejected: { label: "Rechazada", className: "bg-red-100 text-red-600" },
};

/**
 * PlayerCard — contrato: campos de v_scouting_athletes
 * { id, full_name, avatar_url, sport, position, category, country, age, height, points, profile_complete }
 */
const PlayerCard = React.forwardRef(function PlayerCard(
  {
    player,
    isSelected,
    onClick,
    contactStatus = null,
    controlsId,
  },
  ref,
) {
  if (!player) return null;

  const contactBadge = CONTACT_STATUS_BADGE[contactStatus] || null;

  const {
    full_name = "Sin nombre",
    avatar_url = "",
    sport = "",
    position = "Sin posición",
    category = "Amateur",
    country = "",
    age = 0,
    height = 0,
    points = 0,
    profile_complete = false,
    score = null,
    reasons = null,
  } = player;
  const matchScore = typeof score === "number" && score > 0 ? score : null;
  const matchReasons = Array.isArray(reasons) ? reasons : [];

  const sportColor = SPORT_COLORS[sport] || "bg-gray-100 text-gray-600";
  const categoryColor =
    CATEGORY_COLORS[category] || "bg-gray-100 text-gray-600";

  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={isSelected}
      aria-controls={controlsId}
      className={`w-full min-h-11 bg-white rounded-xl p-4 text-left shadow-sm border-2 cursor-pointer transition-all duration-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
        isSelected
          ? "border-blue-500 shadow-lg"
          : "border-transparent hover:border-gray-200"
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <img
            src={avatar_url || defaultAvatar}
            alt={full_name}
            onError={(e) => {
              e.currentTarget.src = defaultAvatar;
            }}
            className="w-14 h-14 rounded-full object-cover border-2 border-gray-100"
          />
          {profile_complete && (
            <CheckCircle
              size={14}
              className="absolute -bottom-0.5 -right-0.5 text-green-500 bg-white rounded-full"
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Nombre + puntos */}
          <div className="flex items-center justify-between mb-1 gap-2">
            <div className="flex min-w-0 items-center gap-1.5">
              <h3 className="font-semibold text-gray-900 truncate text-sm">
                {full_name}
              </h3>
              {player.featured === true && <FeaturedBadge />}
            </div>
            {matchScore !== null ? (
              <span className="flex items-center gap-1 rounded-full bg-blue-900 px-2 py-0.5 text-[10px] font-bold text-white flex-shrink-0">
                <Sparkles size={10} />
                {matchScore}
              </span>
            ) : (
              points > 0 && (
                <span className="text-xs font-bold text-blue-600 flex-shrink-0">
                  {points} pts
                </span>
              )
            )}
          </div>

          {/* Sport + posicion */}
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {sport && (
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${sportColor}`}
              >
                {sport}
              </span>
            )}
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${categoryColor}`}
            >
              {category}
            </span>
            {contactBadge && (
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-semibold ${contactBadge.className}`}
              >
                {contactBadge.label}
              </span>
            )}
          </div>

          {/* Posición + país */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span className="truncate">{position}</span>
            {country && (
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <MapPin size={11} />
                <span>{country}</span>
              </div>
            )}
          </div>

          {matchReasons.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {matchReasons.slice(0, 3).map((reason, idx) => (
                <span
                  key={`${reason}-${idx}`}
                  className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700"
                >
                  {reason}
                </span>
              ))}
            </div>
          )}

          {/* Stats */}
          <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-2 gap-2 text-xs">
            <div className="text-center">
              <div className="font-semibold text-gray-800">
                {age > 0 ? age : "—"}
              </div>
              <div className="text-gray-400">Años</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-800">
                {formatHeight(height)}
              </div>
              <div className="text-gray-400">Altura</div>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
});

export default PlayerCard;
